import React, { useState, useMemo } from "react";
import { LuSquareCheck } from "react-icons/lu";
import { useDashboardTooltip } from "./DashboardTooltip";

const DONUT_COLORS = [
  "#2563eb",
  "#0d9488",
  "#6366f1",
  "#f59e0b",
  "#ec4899",
  "#10b981",
  "#8b5cf6",
  "#0284c7",
  "#f97316",
  "#64748b",
];

export default function TaskDonutCard({
  tasks = [],
  loading = false,
}) {
  const [activeIndex, setActiveIndex] = useState(null);
  const { showTooltip, moveTooltip, hideTooltip } = useDashboardTooltip();

  const taskData = useMemo(() => {
    return tasks.map((t, idx) => ({
      user: t.user || `User ${idx + 1}`,
      tasks: Number(t.tasks ?? t.assigned_tasks ?? 0),
      overdue: Number(t.overdue_tasks ?? 0),
      color: DONUT_COLORS[idx % DONUT_COLORS.length],
    }));
  }, [tasks]);

  const totalTasks = useMemo(() => {
    return taskData.reduce((sum, item) => sum + item.tasks, 0);
  }, [taskData]);

  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  const gap = taskData.length > 1 ? 2 : 0;

  // Compute SVG slices
  const slices = useMemo(() => {
    if (totalTasks === 0) return [];
    let accumulated = 0;
    return taskData.map((item, idx) => {
      const pct = (item.tasks / totalTasks) * 100;
      const segLen = (pct / 100) * circumference;
      const dashLen = Math.max(0.5, segLen - gap);
      const offset = -(accumulated + gap / 2);
      accumulated += segLen;

      return {
        ...item,
        pct: pct.toFixed(1),
        dashLen: dashLen.toFixed(2),
        spaceLen: (circumference - dashLen).toFixed(2),
        offset: offset.toFixed(2),
      };
    });
  }, [taskData, totalTasks, circumference, gap]);

  const centerNum = activeIndex !== null ? slices[activeIndex]?.tasks : totalTasks;
  const centerLabel =
    activeIndex !== null
      ? slices[activeIndex]?.user.split(" ")[0]
      : "Total Tasks";

  const handleSliceClick = (idx) => {
    setActiveIndex((prev) => (prev === idx ? null : idx));
  };

  return (
    <div className="card c4" data-testid="task-user-table-card">
      <div className="head">
        <h2>
          <LuSquareCheck style={{ color: "var(--primary)" }} />
          <span>Tasks by User</span>
        </h2>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>

          <span
            className="pill"
            id="taskTotalPill"
            style={{
              background: "var(--primary-soft)",
              color: "var(--primary)",
              borderColor: "var(--primary-border)",
            }}
          >
            Total: {totalTasks} Tasks
          </span>
        </div>
      </div>

      {loading && taskData.length === 0 ? (
        <div className="skeleton-box" style={{ height: "240px", width: "100%" }} />
      ) : totalTasks === 0 ? (
        <div className="empty-state-card" data-testid="task-empty-state">
          No task/user data available for the selected date range.
        </div>
      ) : (
        <div className="task-donut-body">
          {/* Donut SVG Wrap */}
          <div className="donut-wrap">
            <svg
              className="donut-svg"
              id="taskDonutSvg"
              viewBox="0 0 140 140"
              data-testid="task-donut-svg"
            >
              {slices.map((slice, idx) => {
                const isTarget = activeIndex === idx;
                const isDimmed = activeIndex !== null && !isTarget;

                const tipContent = (
                  <div>
                    <div><b>User:</b> {slice.user}</div>
                    <div><b>Assigned:</b> {slice.tasks} Tasks ({slice.pct}%)</div>
                    {slice.overdue > 0 && (
                      <div style={{ color: "#ef4444" }}><b>Overdue:</b> {slice.overdue} Tasks</div>
                    )}
                  </div>
                );

                return (
                  <circle
                    key={idx}
                    className={`donut-seg ${isTarget ? "highlighted" : ""}`}
                    id={`taskSeg-${idx}`}
                    cx="70"
                    cy="70"
                    r={radius}
                    stroke={slice.color}
                    strokeDasharray={`${slice.dashLen} ${slice.spaceLen}`}
                    strokeDashoffset={slice.offset}
                    style={{
                      opacity: isDimmed ? 0.28 : 1,
                    }}
                    onClick={() => handleSliceClick(idx)}
                    onMouseEnter={(e) => {
                      showTooltip(e, slice.user, tipContent);
                      if (activeIndex === null) setActiveIndex(idx);
                    }}
                    onMouseMove={moveTooltip}
                    onMouseLeave={() => {
                      hideTooltip();
                      if (activeIndex === idx) setActiveIndex(null);
                    }}
                  />
                );
              })}
            </svg>

            <div className="donut-center-info" id="donutCenterInfo">
              <span className="donut-center-num" id="donutCenterNum">
                {centerNum}
              </span>
              <span className="donut-center-label" id="donutCenterLabel" title={centerLabel}>
                {centerLabel}
              </span>
            </div>
          </div>

          {/* Donut Scrollable Legend */}
          <div className="donut-legend" id="taskDonutLegend">
            {slices.map((slice, idx) => {
              const isTarget = activeIndex === idx;

              const tipContent = (
                <div>
                  <div><b>User:</b> {slice.user}</div>
                  <div><b>Assigned:</b> {slice.tasks} Tasks ({slice.pct}%)</div>
                  {slice.overdue > 0 && (
                    <div style={{ color: "#ef4444" }}><b>Overdue:</b> {slice.overdue} Tasks</div>
                  )}
                </div>
              );

              return (
                <div
                  className={`donut-legend-item ${isTarget ? "active" : ""}`}
                  key={idx}
                  id={`taskLegend-${idx}`}
                  onClick={() => handleSliceClick(idx)}
                  onMouseEnter={(e) => {
                    showTooltip(e, slice.user, tipContent);
                    if (activeIndex === null) setActiveIndex(idx);
                  }}
                  onMouseMove={moveTooltip}
                  onMouseLeave={() => {
                    hideTooltip();
                    if (activeIndex === idx) setActiveIndex(null);
                  }}
                >
                  <div className="donut-legend-left">
                    <span
                      className="donut-legend-dot"
                      style={{ background: slice.color }}
                    />
                    <span className="donut-legend-label" title={slice.user}>
                      {slice.user}
                    </span>
                  </div>
                  <div className="donut-legend-right">
                    <span className="donut-legend-val">{slice.tasks}</span>
                    <span className="donut-legend-pct">{slice.pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
